/**
 * B709 leg 1 — the `/memberships` controller: whose page (`?tenant=` or a
 * site key), the guest door (`session.guest` mints the CUSTOMER token and
 * hands back the venue's name and logo), then `membership.plan.list` under
 * that token — a buyer sees only the plans on sale. No storage, no cart: the
 * page reads and compares; buying waits on B709 (ii).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { createApiClient } from "../lib/api.js";
import { resolveSiteKey } from "../lib/boot.js";
import { meVenueFacts, type MeVenueFacts } from "../lib/me.js";
import { membershipsOffer, offerPlans, type MembershipsRoute, type OfferPlan, type Term } from "../lib/membershipsPage.js";
import { MembershipsPage, type MembershipsState } from "./membershipsPage.js";

interface Caller {
  call(op: string, fields: Record<string, unknown>): Promise<unknown>;
}

const LOAD_REFUSED = "The memberships could not be loaded just now. Try again in a moment.";

export function MembershipsApp(props: {
  readonly route: MembershipsRoute;
  readonly apiBaseUrl: string;
  /** A test seam: the API client for a tenant, with or without the guest token. */
  readonly client?: (tenantId: string, token?: string) => Caller;
}): ReactElement {
  const client = useCallback(
    (tenantId: string, token?: string): Caller =>
      props.client?.(tenantId, token) ??
      (createApiClient({ baseUrl: props.apiBaseUrl, tenantId, ...(token !== undefined ? { token: () => token } : {}) }) as unknown as Caller),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam is fixed for the page's life
    [props.apiBaseUrl],
  );
  const [scope, setScope] = useState<{ readonly tenantId: string; readonly channelId: string } | undefined | "unknown">(() =>
    props.route.tenantId !== undefined ? { tenantId: props.route.tenantId, channelId: "web" } : props.route.siteKey !== undefined ? undefined : "unknown",
  );
  useEffect(() => {
    if (scope !== undefined || props.route.siteKey === undefined) return;
    void resolveSiteKey(props.apiBaseUrl, props.route.siteKey).then((ch) => setScope(ch ?? "unknown"));
  }, [scope, props.route.siteKey, props.apiBaseUrl]);

  const [venue, setVenue] = useState<MeVenueFacts>({});
  const [plans, setPlans] = useState<readonly OfferPlan[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [term, setTerm] = useState<Term>("ANNUAL");
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (typeof scope !== "object") return;
    let gone = false;
    setFailed(false);
    void (async () => {
      try {
        const guest = (await client(scope.tenantId).call("session.guest", { channelId: scope.channelId })) as { token?: unknown };
        if (gone) return;
        setVenue(meVenueFacts(guest));
        const token = typeof guest.token === "string" ? guest.token : undefined;
        const read = offerPlans(await client(scope.tenantId, token).call("membership.plan.list", {}));
        if (!gone) setPlans(read);
      } catch {
        if (!gone) setFailed(true);
      }
    })();
    return () => {
      gone = true;
    };
  }, [scope, client, attempt]);

  const offer = useMemo(() => (plans === undefined ? undefined : membershipsOffer(plans, term)), [plans, term]);
  const state: MembershipsState =
    scope === "unknown"
      ? { kind: "error", message: "This page doesn't know whose memberships to show — open it from the business's own site." }
      : failed
        ? { kind: "error", message: LOAD_REFUSED }
        : plans === undefined
          ? { kind: "loading" }
          : offer === undefined
            ? { kind: "empty" }
            : { kind: "ready", offer };
  const firstTier = offer?.tiers[0]?.tierId ?? offer?.limited[0]?.tierId;
  const tenantId = typeof scope === "object" ? scope.tenantId : undefined;
  return (
    <MembershipsPage
      venue={venue}
      state={state}
      term={offer !== undefined && offer.terms.includes(term) ? term : (offer?.terms[0] ?? term)}
      picked={picked ?? firstTier}
      onTerm={setTerm}
      onPick={setPicked}
      onRetry={() => setAttempt((n) => n + 1)}
      ticketsUrl={tenantId !== undefined ? `/?tenant=${encodeURIComponent(tenantId)}` : undefined}
    />
  );
}
